import { ALL_ITEMS, type EverydayItem } from "./catalog";

export type Equivalent = { item: EverydayItem };

const TOLERANCE = 0.15;

// The everyday item for a price (Toman): the closest item within 15% of the price (or, for an item with a known price
// band, inside that band), preferring one no other plan uses yet. Never a count of items: when nothing fits, none.
export function pickEquivalent(amount: number, used: Set<string> = new Set()): Equivalent | null {
  if (!(amount > 0)) return null;
  const off = (i: EverydayItem) => Math.abs(i.price - amount) / amount;
  const fits = (i: EverydayItem) => (i.band ? amount >= i.band[0] && amount <= i.band[1] : off(i) <= TOLERANCE);
  const near = ALL_ITEMS.filter(fits).sort((a, b) => off(a) - off(b));
  const item = near.find((i) => !used.has(i.image)) ?? near[0];
  return item ? { item } : null;
}

// One equivalent per plan, cheapest plan first, without repeating an image when there is a choice.
export function pickEquivalents(amounts: (number | null | undefined)[]): (Equivalent | null)[] {
  const used = new Set<string>();
  return amounts.map((amount) => {
    const eq = amount == null ? null : pickEquivalent(amount, used);
    if (eq) used.add(eq.item.image);
    return eq;
  });
}

// Equivalents keyed by plan id for a PlanView list (cheapest first). Item prices are in Toman, so other
// currencies and logged-out visitors (no price) get none.
export function planEquivalents(plans: { id: string; total: number | null }[], currencyCode?: string | null): Record<string, Equivalent | null> {
  if (currencyCode !== "IRT") return {};
  const eqs = pickEquivalents(plans.map((p) => p.total));
  return Object.fromEntries(plans.map((p, i) => [p.id, eqs[i]]));
}

export const equivalentImage = ({ item }: Equivalent) => `/img/equivalents/${item.image}.webp`;

// Self-check, runs once in dev.
if (import.meta.env?.DEV) {
  const [m1, m3, m6, m12] = pickEquivalents([129_000, 299_000, 499_000, 790_000]);
  console.assert(m1?.item.slug === "energy" && m3?.item.slug === "milkshake" && m6?.item.slug === "burger" && m12?.item.slug === "birthdaycake", "pickEquivalents closest", [m1, m3, m6, m12]);
  console.assert(pickEquivalent(199_000)?.item.slug === "donut" && pickEquivalent(850_000)?.item.slug === "birthdaycake", "pickEquivalent closest");
  console.assert(pickEquivalent(950_000)?.item.slug === "pizza" && pickEquivalent(380_000)?.item.slug === "croissant" && pickEquivalent(420_000)?.item.slug === "burger", "price bands");
  console.assert(pickEquivalent(20_000) === null && pickEquivalent(0) === null && pickEquivalent(4_100_000) === null, "nothing fits: no equivalent, never a count");
}
